import { useState, useEffect, useContext, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";

import {
  Typography,
  Spin,
  App,
  Modal,
  Button,
  Flex,
  Tag,
  Tooltip,
  Space,
  Upload,
  Dropdown,
  Pagination,
  notification,
} from "antd";

import HomeIcon from "@/icons/HomeIcon";
import FolderAddIcon from "@/icons/FolderAddIcon";
import DocumentAddIcon from "@/icons/DocumentAddIcon";
import PencilIcon from "@/icons/PencilIcon";
import TrashIcon from "@/icons/TrashIcon";
import UploadIcon from "@/icons/UploadIcon";

import FolderModal from "@/components/modals/FolderModal";
import FileModal from "@/components/modals/FileModal";
import FileLinkModal from "@/components/modals/FileLinkModal";
import FilesTable from "@/components/tables/FilesTable";
import CatalogContext from "@/components/views/CatalogView/context";

import { UserContext } from "@/context/UserContext";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import {
  getFolder,
  deleteFolder,
  getFolderContent,
  deleteFile,
  globalSearchFiles,
  deleteFiles,
  getFilePage,
} from "@/api/files";

import routes from "@/routes";

import { getPluralizedWord } from "@/utils";

const LOCAL_STORAGE_KEY = "foldersTableParams";

const DEFAULT_SORTER = [
  {
    field: "created_at",
    columnKey: "created_at",
    order: "descend",
  },
];

const DEFAULT_TABLE_PARAMS = {
  pagination: {
    current: 1,
    pageSize: 10,
    placement: ["bottomLeft"],
    showSizeChanger: true,
  },
  sorter: DEFAULT_SORTER,
};

const normalizeSorter = (sorter) => {
  if (!sorter) return [];

  const sorterArray = Array.isArray(sorter) ? sorter : [sorter];

  return sorterArray
    .filter(({ field, columnKey, order }) => order && (field || columnKey))
    .map(({ field, columnKey, order }) => ({
      field: field || columnKey,
      columnKey: columnKey || field,
      order,
    }));
};

const parseSortString = (sort) => {
  if (!sort || typeof sort !== "string") return [];

  return sort
    .split(",")
    .filter(Boolean)
    .map((field) => {
      const isDescend = field.startsWith("-");
      const normalizedField = isDescend ? field.slice(1) : field;

      return {
        field: normalizedField,
        columnKey: normalizedField,
        order: isDescend ? "descend" : "ascend",
      };
    });
};

const getSortParam = (sorter) => {
  const sortString = normalizeSorter(sorter)
    .map(({ field, order }) => (order === "descend" ? `-${field}` : field))
    .join(",");

  return sortString || undefined;
};

const getStoredTableParams = (userUuid) => {
  try {
    const savedParams = JSON.parse(
      localStorage.getItem(`${userUuid}_${LOCAL_STORAGE_KEY}`),
    );

    const savedSorter =
      normalizeSorter(savedParams?.sorter).length > 0
        ? normalizeSorter(savedParams.sorter)
        : parseSortString(savedParams?.sort);

    return {
      ...DEFAULT_TABLE_PARAMS,
      ...savedParams,
      pagination: {
        ...DEFAULT_TABLE_PARAMS.pagination,
        ...savedParams?.pagination,
      },
      sorter: savedSorter.length > 0 ? savedSorter : DEFAULT_SORTER,
    };
  } catch {
    return DEFAULT_TABLE_PARAMS;
  }
};

const uploadsProps = {
  name: "file",
  action: "/api/file/upload",
  multiple: false,
  showUploadList: false,
};

const { Title, Text } = Typography;
const { Dragger } = Upload;

const Folder = () => {
  const { user } = useContext(UserContext);
  const { id } = useParams();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const currentSearchParams = new URLSearchParams(location.search);
  const isAdmins = location.pathname.startsWith("/administration");

  const {
    file_type,
    units_uuids,
    fetchCatalog,
    unit_uuid,
    author_uuid,
    search,
    setSearch,
    units,
    unit_in_path,
    isCompactCatalog,
  } = useContext(CatalogContext);

  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const [filesPending, setFilesPending] = useState(false);

  const [selectFiles, setSelectFiles] = useState([]);

  // Окно создания/редактирования папки
  const [isModalFolderShow, setIsModalFolderShow] = useState(false);
  const [currentFolder, setCurrentFolder] = useState(null);

  // Окно создания/редактирования файла
  const [isModalFileShow, setIsModalFileShow] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);

  // Таблица с содержимым папки
  const [contentData, setContentData] = useState([]);
  const [contentPending, setContentPending] = useState(true);
  const [tablePending, setTablePending] = useState(false);

  // Инициализация пагинации из localStorage
  const [tableParams, setTableParams] = useState(() =>
    getStoredTableParams(user.user_uuid),
  );

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    contentData,
    tableParams.pagination.pageSize,
  ]);

  const [isSystemModalShow, setIsSystemModalShow] = useState(false);
  const uploadHiddenBtnRef = useRef(null);

  const addFileMenu = {
    items: [
      { key: "device", label: "Добавить файл с устройства" },
      { key: "system", label: "Добавить файл из системы" },
    ],
    onClick: ({ key }) => {
      if (key === "device") {
        uploadHiddenBtnRef.current?.click();
      } else if (key === "system") {
        setIsSystemModalShow(true);
      }
    },
  };

  const initWithFileUuid = async (file_uuid) => {
    try {
      const savedParams = getStoredTableParams(user.user_uuid);

      const pageSize = savedParams?.pagination?.pageSize || 10;

      const { page } = await getFilePage(
        {
          entity_uuid: file_uuid,
          perpage: pageSize,
          sort: getSortParam(savedParams?.sorter),
        },
        isAdmins,
      );

      await fetchFolderContent(
        {
          current: page || null,
          pageSize,
        },
        savedParams?.sorter,
        { ignoreFilters: true },
      );

      setSelectFiles([file_uuid]);
    } catch (e) {
      await fetchFolderContent();
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      if (id === "catalog" || id === "search") {
        setData({});
      } else {
        const data = await getFolder(id, isAdmins);
        setData(data);
      }
    } catch (error) {
      navigate(
        `${
          isAdmins ? routes.administration.folder() : routes.folder()
        }?${currentSearchParams.toString()}`,
        {
          replace: true,
        },
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFolderContent = async (pagination, sorter, options = {}) => {
    const { ignoreFilters = false } = options;

    setTablePending(true);

    const nextSorter =
      sorter === undefined
        ? normalizeSorter(tableParams.sorter)
        : normalizeSorter(sorter);

    const requestedPage = pagination?.current || tableParams.pagination.current;
    const requestedPageSize =
      pagination?.pageSize || tableParams.pagination.pageSize;

    const params = {
      uuid: id === "catalog" || id === "search" ? "" : id,
      page: requestedPage,
      perpage: requestedPageSize,

      ...(!ignoreFilters && unit_uuid && { unit_uuid }),
      ...(!ignoreFilters && author_uuid && { author_uuid }),
      ...(!ignoreFilters &&
        units_uuids && {
          units_uuids: units_uuids.map(({ value }) => value),
        }),
      ...(!ignoreFilters && search && id === "search" && { search }),
      ...(!ignoreFilters && file_type && { type_filter: file_type }),
    };

    const sort = getSortParam(nextSorter);

    if (sort) {
      params.sort = sort;
    }

    try {
      const { contents, total, page, perPage } = search
        ? await globalSearchFiles(params, isAdmins)
        : await getFolderContent(params, isAdmins);

      const lastPage = Math.max(1, Math.ceil(total / perPage));

      if (requestedPage > lastPage) {
        await fetchFolderContent(
          {
            current: lastPage,
            pageSize: perPage,
          },
          nextSorter,
          options,
        );

        return;
      }

      setContentData(contents || []);

      const updatedTableParams = {
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total,
          current: page,
          pageSize: perPage,
        },
        sorter: nextSorter,
      };
      setTableParams(updatedTableParams);

      // Сохраняем параметры пагинации в localStorage
      localStorage.setItem(
        `${user.user_uuid}_${LOCAL_STORAGE_KEY}`,
        JSON.stringify(updatedTableParams),
      );
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setTablePending(false);
    }
  };

  // initial fetch
  useEffect(() => {
    const file_uuid = new URLSearchParams(location.search).get("file_uuid");

    const initFolder = async () => {
      if (id !== "search" && search) {
        setSearch("");
      }

      setContentData([]);
      setContentPending(true);
      setSelectFiles([]);

      await fetchData();

      if (file_uuid) {
        await initWithFileUuid(file_uuid);
      } else {
        await fetchFolderContent(
          {
            current: tableParams.pagination.current,
            pageSize: tableParams.pagination.pageSize,
          },
          tableParams.sorter,
        );
      }

      setContentPending(false);
    };

    initFolder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.search]);

  useEffect(() => {
    const newParams = {
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: 1,
      },
    };

    setTableParams(newParams);

    localStorage.setItem(
      `${user.user_uuid}_${LOCAL_STORAGE_KEY}`,
      JSON.stringify(newParams),
    );

    fetchFolderContent({ current: 1 });
    // eslint-disable-next-line
  }, [units_uuids, author_uuid, search]);

  const handleAddNewFolder = () => {
    setCurrentFolder({
      parent_uuid: data.uuid,
      unit_uuid: unit_uuid || data.unit_uuid,
    });
    setIsModalFolderShow(true);
  };

  const handleEditFolder = () => {
    setCurrentFolder(data);
    setIsModalFolderShow(true);
  };

  const handleDeleteFolder = () => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить папку ${data.name} ?`,
      content: "Все содержимое папки будет удалено.",
      onOk() {
        handleConfirmDeleteFolder();
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  const handleConfirmDeleteFolder = async () => {
    const parent_uuid = data.parent_uuid || "catalog";
    try {
      await deleteFolder({ uuid: data.uuid });
      fetchCatalog();
      // перейти в родительскую папку

      navigate(
        `${
          isAdmins
            ? routes.administration.folder(parent_uuid)
            : routes.folder(parent_uuid)
        }?${currentSearchParams.toString()}`,
        { replace: true },
      );
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  // Обработчик сохранения папки
  const handleSaveFolder = () => {
    setIsModalFolderShow(false);
    fetchCatalog();
  };

  const handleDropFile = ({ file }) => {
    const { status } = file;

    if (file.status === "uploading") {
      setFilesPending(true);
      return;
    }

    if (status === "done") {
      setFilesPending(false);
      const fileObj = file.response.files[0];
      const name = fileObj.original_name.split(".").slice(0, -1).join(".");
      setCurrentFile({
        ...fileObj,
        name,
        folder_uuid: data.uuid,
        unit_uuid: unit_uuid || data.unit_uuid,
        isSystem: false,
      });
      setIsModalFileShow(true);
    } else if (status === "error") {
      message.error(
        `Загрузка файла ${file.name} не удалась. Попробуйте еще раз.`,
      );
      setFilesPending(false);
    }
  };

  const handleSaveFile = () => {
    setIsModalFileShow(false);
    fetchFolderContent();
  };

  const handleSaveFileLink = async (payload) => {
    const name =
      (payload.name || "Файл из системы").split(".").slice(0, -1).join(".") ||
      payload.name ||
      "Файл из системы";

    setCurrentFile({
      ...payload,
      name,
      folder_uuid: data.uuid,
      unit_uuid: unit_uuid || data.unit_uuid,
      isSystem: true,
    });

    setIsSystemModalShow(false);
    setIsModalFileShow(true);
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    fetchFolderContent(pagination, sorter);
  };

  const handlePaginationChange = (current, pageSize) => {
    // При изменении pageSize сбрасываем страницу на 1
    const shouldResetPage = pageSize !== tableParams.pagination.pageSize;

    const newPagination = {
      ...tableParams.pagination,
      current: shouldResetPage ? 1 : current,
      pageSize,
    };

    fetchFolderContent(newPagination, tableParams.sorter);
  };

  const handleEditFile = (item) => {
    setCurrentFile(item);
    setIsModalFileShow(true);
  };

  // Обработчик удаления файла
  // Или папки (заложила возможность потом добавить контролы для папок)
  const handleDeleteFile = (item) => {
    const { name, element_type } = item;
    Modal.confirm({
      title: `Вы уверены, что хотите удалить ${
        element_type === "file" ? "файл" : "папку"
      } ${name} ?`,
      content:
        element_type === "file"
          ? "Это действие нельзя будет отменить. Данные будут удалены."
          : "Это действие нельзя будет отменить. Все содержимое папки будет удалено.",
      onOk() {
        handleConfirmDeleteFile(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем файл после подтверждения
  const handleConfirmDeleteFile = async ({ element_type, uuid }) => {
    setTablePending(true);
    try {
      element_type === "folder"
        ? await deleteFolder({ uuid })
        : await deleteFile({ uuid });

      await fetchFolderContent();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setTablePending(false);
    }
  };

  // Обработчик удаления выбранных файлов
  const handleDeleteFiles = () => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить ${selectFiles.length} ${getPluralizedWord(selectFiles.length, ["файл", "файла", "файлов"])}`,
      content:
        "Это действие нельзя будет отменить. Данные будут удалены окончательно.",
      onOk() {
        handleConfirmDeleteFiles();
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем файлы после подтверждения
  const handleConfirmDeleteFiles = async () => {
    setTablePending(true);
    try {
      const { message } = await deleteFiles(selectFiles);
      notification.open({
        message,
        icon: null,
        duration: 4,
        pauseOnHover: true,
        placement: "bottomRight",
      });
      setSelectFiles([]);

      await fetchFolderContent();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setTablePending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="spin">
        <Spin size="large" />
      </div>
    );
  }

  return (
    !isLoading &&
    data && (
      <>
        <Flex vertical gap={20} style={{ flex: 1, minHeight: 0 }}>
          {!search && (
            <Flex vertical gap={10}>
              <Flex gap={10} align="flex-start" wrap>
                <Flex
                  gap={10}
                  wrap
                  align="flex-start"
                  style={{ marginTop: 10 }}
                >
                  <Title level={3} style={{ margin: 0, flex: 1 }}>
                    {data.name ? (
                      data.name
                    ) : (
                      <Space>
                        <HomeIcon /> Папка
                      </Space>
                    )}
                  </Title>

                  {data.name && <Tag color="blue">{data.unit?.name}</Tag>}
                </Flex>

                <Flex
                  justify="flex-end"
                  align="center"
                  gap={10}
                  wrap
                  style={{ marginLeft: "auto" }}
                >
                  {!!selectFiles.length && (
                    <div style={{ whiteSpace: "nowrap" }}>
                      Выбрано {selectFiles.length}{" "}
                      {getPluralizedWord(selectFiles.length, [
                        "файл",
                        "файла",
                        "файлов",
                      ])}{" "}
                      <Button
                        color="primary"
                        variant="link"
                        icon={<TrashIcon />}
                        onClick={handleDeleteFiles}
                      >
                        Удалить
                      </Button>
                    </div>
                  )}
                  {id !== "catalog" && !data.readOnly && (
                    <Space.Compact>
                      <Tooltip placement="bottomRight" title="Добавить папку">
                        <Button
                          size="large"
                          // type="primary"
                          icon={<FolderAddIcon style={{ fontSize: 18 }} />}
                          onClick={handleAddNewFolder}
                        ></Button>
                      </Tooltip>
                      <Dropdown
                        menu={addFileMenu}
                        placement="bottomRight"
                        trigger={["click"]}
                      >
                        <Tooltip placement="bottomRight" title="Добавить файл">
                          <Button
                            size="large"
                            loading={filesPending}
                            icon={<DocumentAddIcon style={{ fontSize: 18 }} />}
                          />
                        </Tooltip>
                      </Dropdown>
                      <Tooltip
                        placement="bottomRight"
                        title="Редактировать папку"
                      >
                        <Button
                          size="large"
                          // type="primary"
                          icon={<PencilIcon style={{ fontSize: 18 }} />}
                          onClick={handleEditFolder}
                        ></Button>
                      </Tooltip>

                      <Tooltip placement="bottomRight" title="Удалить папку">
                        <Button
                          size="large"
                          icon={<TrashIcon style={{ fontSize: 18 }} />}
                          onClick={handleDeleteFolder}
                        ></Button>
                      </Tooltip>
                    </Space.Compact>
                  )}
                </Flex>
              </Flex>

              {data.description && <Text>{data.description}</Text>}
            </Flex>
          )}

          <Flex vertical gap={20} style={{ flex: 1, minHeight: 0 }}>
            {contentPending ? (
              <div className="spin">
                <Spin size="large" />
              </div>
            ) : !!contentData?.length ? (
              <>
                <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
                  <FilesTable
                    unit_uuid={unit_in_path}
                    data={contentData}
                    loading={tablePending}
                    pagination={false}
                    isCompactCatalog={isCompactCatalog}
                    sortedInfo={tableParams.sorter}
                    onChange={handleTableChange}
                    onEditItem={handleEditFile}
                    onDeleteItem={handleDeleteFile}
                    show_description
                    resize
                    scroll={{ x: "100%", y: tableHeight - headerHeight }}
                    setSelectFiles={setSelectFiles}
                    rowSelection={{
                      type: "checkbox",
                      selectedRowKeys: selectFiles,
                      preserveSelectedRowKeys: true,
                      onChange: (records) => {
                        setSelectFiles(records);
                      },
                      getCheckboxProps: (record) => ({
                        disabled:
                          record.element_type !== "file" || record.readOnly,
                      }),
                      columnTitle: "",
                      columnWidth: 40,
                    }}
                  />
                </div>

                <div style={{ minHeight: 32 }}>
                  <Pagination
                    {...tableParams.pagination}
                    onChange={handlePaginationChange}
                  />
                </div>
              </>
            ) : search ? (
              <div style={{ padding: 20 }}>
                <Text>По вашему запросу ничего не найдено.</Text>
              </div>
            ) : data.readOnly || id === "catalog" ? (
              <div style={{ color: "#777" }}>Пустая папка</div>
            ) : (
              <Dragger
                {...uploadsProps}
                onChange={handleDropFile}
                onDrop={(e) =>
                  console.log("Dropped files", e.dataTransfer.files)
                }
                className="h-100"
              >
                <Spin size="large" spinning={filesPending}>
                  <div style={{ padding: 20 }}>
                    <p className="ant-upload-drag-icon">
                      <UploadIcon />
                    </p>
                    <p className="ant-upload-text">
                      Эта папка пуста. Можете нажать или&nbsp;перетащить файл
                      в&nbsp; эту область для&nbsp;загрузки.
                    </p>
                    <p className="ant-upload-hint">
                      Запрещено загружать конфиденциальные данные
                      или&nbsp;файлы, нарушающие правила.
                    </p>
                  </div>
                </Spin>
              </Dragger>
            )}
          </Flex>
        </Flex>

        {isModalFolderShow && (
          <FolderModal
            data={currentFolder}
            visible={isModalFolderShow}
            units={units}
            onSave={handleSaveFolder}
            onCancel={() => setIsModalFolderShow(false)}
          />
        )}

        {isModalFileShow && (
          <FileModal
            data={currentFile}
            visible={isModalFileShow}
            units={units}
            onSave={handleSaveFile}
            onCancel={() => setIsModalFileShow(false)}
          />
        )}

        {isSystemModalShow && (
          <FileLinkModal
            visible={isSystemModalShow}
            onSave={handleSaveFileLink}
            onCancel={() => setIsSystemModalShow(false)}
            isAdmins={isAdmins}
          />
        )}

        <Upload
          {...uploadsProps}
          onChange={handleDropFile}
          style={{ display: "none" }}
        >
          <Button ref={uploadHiddenBtnRef} style={{ display: "none" }} />
        </Upload>
      </>
    )
  );
};

export default Folder;
